/* eslint-disable import/order */
import {
  FC,
  lazy,
  useEffect,
  Suspense,
  useState,
  useLayoutEffect,
  useRef
} from 'react';

import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

import { safeSessionStorage } from '../../../../helpers/utils/safeStorage';
import { isImportBlockedByTariff } from '../../../../helpers/utils/tariff';
import { routeNames } from '../../../../shared/common/constants/routes';

import PermissionWrapper from '../../../../shared/common/hoc/PermissionWrapper';
import { useActions } from '../../../../shared/common/hooks/useActions';
import { useAppDispatch, useTypedSelector } from '../../../../shared/common/hooks/useTypedSelector';

import Loading from '../../../../shared/common/components/reports/cohort/components/Loading';

import AccessEnded from '../../../../pages/access-ended/AccessEnded';
import ConfirmEmail from '../../../../pages/auth/confirm-email';
import Login from '../../../../pages/auth/login';
import Reset from '../../../../pages/auth/reset';
import Restore from '../../../../pages/auth/restore';
import Signup from '../../../../pages/auth/signup';
import Bill from '../../../../pages/bill';
import CrmActivity from '../../../../pages/crm/activity';
import CrmAnalytics from '../../../../pages/crm/analytics';
import CrmDetails from '../../../../pages/crm/details';
import CrmOrders from '../../../../pages/crm/orders';
import CrmPayments from '../../../../pages/crm/payments';
import CrmSchools from '../../../../pages/crm/schools';
import CrmTariffs from '../../../../pages/crm/tariffs';
import CrmUsers from '../../../../pages/crm/users';
import ErrorPage from '../../../../pages/error';
import Export from '../../../../pages/export';
import Home from '../../../../pages/home/Home';
import NoAccess from '../../../../pages/no-access';
import NotFound from '../../../../pages/not-found';
import OrderReports from '../../../../pages/order-reports';
import Partner from '../../../../pages/partner/Partner';
import PayPage from '../../../../pages/pay';
import PaymentReports from '../../../../pages/payment-reports';
import PaymentError from '../../../../pages/paymentResults/PaymentError';
import PaymentSuccess from '../../../../pages/paymentResults/PaymentSuccess';
import Profile from '../../../../pages/profile';
import Cohort from '../../../../pages/reports/cohort';
import Hit from '../../../../pages/reports/hit';
import HitStat from '../../../../pages/reports/hit-stat';
import Orders from '../../../../pages/reports/orders';
import Payments from '../../../../pages/reports/payments';
import Regs from '../../../../pages/reports/regs';
import Session from '../../../../pages/reports/session';
import Users from '../../../../pages/reports/users';
import Visitors from '../../../../pages/reports/visitors';
import WebinarAttendance from '../../../../pages/reports/webinar-attendance';
import WebinarComments from '../../../../pages/reports/webinar-comments';
import WebinarQuality from '../../../../pages/reports/webinar-quality';
import LoadData from '../../../../pages/settings/school/load-data';
import SchoolAdd from '../../../../pages/settings/school/school-add';
import SchoolEdit from '../../../../pages/settings/school/school-edit';
import Staff from '../../../../pages/settings/school/staff';
import TrackScript from '../../../../pages/settings/school/track-script';
import ConnectBizon from '../../../../pages/settings/webinar/connect-bizon';
import WebinarList from '../../../../pages/settings/webinar/list';
import TelegramConnect from '../../../../pages/telegram-connect';
import WebinarReports from '../../../../pages/webinar-reports/WebinarReports';
import PartnerCrm from '../../../../pages/crm/partner/PartnerCrm';
import { Tasks } from '../../../../pages/tasks';
import SuccessAddSchool from '../../../../pages/school/SuccessAddSchool';
import Visits from '../../../../pages/reports/visits/Visits';
import LastData from '../../../../pages/settings/LastData/LastData';
import YandexMetricsPage from '../../../../pages/settings/yandexMetrics';
import Dashboards from '../../../../pages/dashboards';
import CrmRequests from '../../../../pages/crm/requests';
import ImportPage from '../../../../pages/import';
import AdsRules from '../../../../pages/ads-rules';
import PurchasePrediction from '../../../../pages/purchase-prediction/PurchasePrediction';
import Aps from '../../../../pages/aps';
import ProductsPage from '../../../../pages/products/ProductsPage';
import ProductPage from '../../../../pages/products/ProductPage';
import NrRedirect from '../../../../pages/nr-redirect/NrRedirect';

import { trackNavigation } from '../../../../helpers/utils/analytics';
import { sendPageHit } from '../../../../helpers/utils/hits';
import { partnerCookies } from '../../../../helpers/utils/partnerCookies';

import Ad from '../../../../pages/settings/ads/ad/Ad';
import { setMainDashboardThunk } from '../../redux/slices/reports/params/reports/reportsSlice';
import { CONST_HASH_MAIN_DASHBOARD } from '../../../../shared/common/constants/staticLinkDashboard';
import { QUERY_NAME_DASHBOARD } from '../../../../shared/common/constants/query-params';

const ADReports = lazy(() => import('../../../../pages/ad-reports'));

// const availebleRouteWithoutTG = [
//   routeNames.login,
//   routeNames.signup,
//   routeNames.resetPassword,
//   routeNames.restorePassword,
//   routeNames.notFound,
//   routeNames.error,
//   routeNames.schoolAdded,
//   routeNames.payment,
//   routeNames.paymentSuccess,
//   routeNames.paymentError,
// ];

// Флаг для отключения проверок на локальном окружении (для разработки)
const IS_LOCAL_DEV = process.env.NODE_ENV === 'development' && 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

export const AppRouter: FC = () => {
  const isFetchUserInfoRef = useRef(false);
  const previousPath = useRef<string>('');
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    partnerCookies();
  }, []);

  const { isGlobalLoading } = useTypedSelector((state) => state.app);
  const { isAuth } = useTypedSelector((state) => state.auth);
  const { selectedSchool, schoolList, tariff } = useTypedSelector((state) => state.currentSchool);
  const { isLoading, main_dashboard, isAdmin, messenger_connect, require_messenger } = useTypedSelector((state) => state.currentUser);

  const { fetchUserAndSchoolInfo, currentSchoolFetchDetails } = useActions();

  // Без привязки мессенджера (Telegram ИЛИ MAX) кабинет не открывается
  const [isRedirectedToMessenger, setIsRedirectedToMessenger] = useState(false);

  useLayoutEffect(() => {
    if (isAuth) {
      fetchUserAndSchoolInfo(navigate, () => isFetchUserInfoRef.current = true);
    }
  }, [isAuth]);

  useLayoutEffect(() => {
    // На локальном окружении и при выключенном требовании проверку пропускаем
    if (IS_LOCAL_DEV || !require_messenger) {
      setIsRedirectedToMessenger(false);
      return;
    }
    const tmp = !messenger_connect && !isLoading && !isGlobalLoading;

    if (isRedirectedToMessenger !== tmp) setIsRedirectedToMessenger(tmp);
  }, [messenger_connect, require_messenger, isLoading]);

  useLayoutEffect(() => {
    if (!isGlobalLoading && !isLoading) {
      const hash = main_dashboard ?? CONST_HASH_MAIN_DASHBOARD;
      dispatch(setMainDashboardThunk(hash));

      // Устанавливаем дашборд из сводки только если в URL нет report_id
      // Это позволяет открывать дашборды по прямой ссылке и сохранять их при обновлении страницы
      if (location.pathname === routeNames.dashboards) {
        const currentReportId = searchParams.get(QUERY_NAME_DASHBOARD);
        if (!currentReportId) {
          setSearchParams({ [QUERY_NAME_DASHBOARD]: hash });
        }
      }
    }
  }, [isGlobalLoading, isLoading]);

  useLayoutEffect(() => {
    if (!isFetchUserInfoRef.current) return;

    const isPartner = window.location.pathname.includes('/users/partner');
    const isProfile = window.location.pathname.includes('users/profile');

    if (location.pathname === routeNames.login) return;

    // Пока мессенджер не привязан, пускаем только на страницу привязки
    if (isRedirectedToMessenger && !IS_LOCAL_DEV) {
      navigate(routeNames.telegramConnect);
      return;
    }

    if (isGlobalLoading) return;

    // Если у пользователя есть школа, то получаем её детали
    if (selectedSchool?.id && !isPartner && !isProfile) {
      currentSchoolFetchDetails(selectedSchool.id);
    }

    // Если у пользователя нет школы и он не на странице партнёрки, перенаправляем на страницу добавления школы
    // На локальном окружении пропускаем эту проверку
    if (!selectedSchool?.id && !IS_LOCAL_DEV) {
      if (!isPartner) {
        navigate(routeNames.schoolAdd);
        return;
      }
    }

    // Если пользователь на главной странице, перенаправляем на дашборды
    if (location.pathname === '/') {
      navigate(routeNames.dashboards);
      return;
    }
  }, [selectedSchool, isRedirectedToMessenger, location.pathname, isGlobalLoading]);


  useEffect(() => {
    sendPageHit();
    trackNavigation(previousPath.current, location.pathname);
    previousPath.current = location.pathname;

    if (location.pathname !== routeNames.telegramConnect) {
      if (location.state) {
        const query = new URLSearchParams(Object.entries(location.state));
        const url = `${location.pathname}?${query}`;
        safeSessionStorage.setItem('activeLink', url);
      } else {
        safeSessionStorage.setItem('activeLink', location.pathname);
      }
    }
  }, [location.pathname]);

  return (
    <Routes>
      <Route path={routeNames.nrRedirect} element={<NrRedirect />} />
      {/* Смена пароля по ссылке из письма - вне проверки isAuth: письмо часто открывают
          в браузере с активной сессией, и тогда маршрут ниже уводил в личный кабинет. */}
      <Route path={routeNames.restorePassword} element={<Restore />} />
      <Route path={routeNames.schoolAdded} element={<SuccessAddSchool />} />
      <Route path={routeNames.payment} element={<PayPage />} />
      <Route path={routeNames.paymentSuccess} element={<PaymentSuccess />} />
      <Route path={routeNames.paymentError} element={<PaymentError />} />
      {isAuth ? (
        <>
          {schoolList && (<>
            <Route path="/" element={<Navigate to={routeNames.home} replace />} />
            <Route path="*" element={<Navigate to={routeNames.home} replace />} />
          </>)}
          <Route path={routeNames.import} element={
            <PermissionWrapper allowedRoles={isAdmin ? ['owner', 'employee'] : ['owner']}>
              {/* На льготном тарифе импорт недоступен даже при прямом переходе по URL */}
              {isImportBlockedByTariff(tariff?.id)
                ? <Navigate to={routeNames.home} replace />
                : <ImportPage />}
            </PermissionWrapper>
          } />
          <Route path={routeNames.home} element={<Home />} />
          <Route path={routeNames.dashboardMonitor} element={<Dashboards />} />
          <Route path={routeNames.dashboards} element={<Dashboards />} />
          <Route path={routeNames.accessEnded} element={<AccessEnded />} />
          <Route path={routeNames.orderReports} element={<OrderReports />} />
          <Route
            path={routeNames.paymentReports}
            element={<PaymentReports />}
          />
          <Route path={routeNames.lastData} element={<LastData />} />
          <Route
            path={routeNames.adReports}
            element={
              <Suspense
                fallback={
                  <Loading loading={true}>
                    <></>
                  </Loading>
                }
              >
                <ADReports />
              </Suspense>
            }
          />
          <Route
            path={routeNames.webinarReports}
            element={<WebinarReports />}
          />
          <Route
            path={routeNames.tasks}
            element={
              <PermissionWrapper>
                <Suspense fallback={<h1>Loading...</h1>}>
                  <Tasks />
                </Suspense>
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.reportVisits}
            element={
              <PermissionWrapper>
                <Suspense fallback={<h1>Loading...</h1>}>
                  <Visits />
                </Suspense>
              </PermissionWrapper>
            }
          />
          <Route path={routeNames.profile} element={<Profile />} />

          <Route
            path={routeNames.schoolEdit}
            element={
              <PermissionWrapper allowedRoles={['owner', 'employee']}>
                <SchoolEdit />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.schoolTrackScript}
            element={
              <PermissionWrapper allowedRoles={['owner', 'employee']}>
                <TrackScript />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.schoolStaff}
            element={
              <PermissionWrapper allowedRoles={['owner', 'employee']}>
                <Staff />
              </PermissionWrapper>
            }
          />

          <Route
            path={routeNames.schoolLoadData}
            element={
              <>
                <LoadData />
              </>
            }
          />
          <Route path={routeNames.schoolAdd} element={<SchoolAdd />} />
          <Route
            path={routeNames.webinarList}
            element={
              <PermissionWrapper allowedRoles={['owner', 'employee']}>
                <WebinarList />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.webinarConnectBizon}
            element={
              <PermissionWrapper allowedRoles={['owner', 'employee']}>
                <ConnectBizon />
              </PermissionWrapper>
            }
          />
          <Route path={routeNames.adsConnect} element={<Ad />} />
          <Route path={routeNames.adsSpend} element={<Ad />} />
          <Route path={routeNames.ad} element={<Ad />} />
          <Route
            path={routeNames.adsRules}
            element={
              <PermissionWrapper allowedRoles={['owner', 'employee']}>
                <AdsRules />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.purchasePrediction}
            element={
              <PermissionWrapper allowedRoles={['superadmin']}>
                <PurchasePrediction />
              </PermissionWrapper>
            }
          />
          <Route path={routeNames.aps} element={<Aps />} />
          <Route path={routeNames.products} element={<ProductsPage />} />
          <Route path={routeNames.productDetail} element={<ProductPage />} />
          <Route
            path={routeNames.yandexMetrics}
            element={
              <PermissionWrapper allowedRoles={['owner', 'employee']}>
                <YandexMetricsPage />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.reportWebinarQuality}
            element={<WebinarQuality />}
          />
          <Route
            path={routeNames.reportWebinarComments}
            element={
              <PermissionWrapper
                allowedRoles={['owner', 'employee']}
              >
                <WebinarComments />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.reportWebinarAttendance}
            element={
              <PermissionWrapper
                allowedRoles={['owner', 'employee']}
              >
                <WebinarAttendance />
              </PermissionWrapper>
            }
          />
          <Route path={routeNames.reportCohort} element={<Cohort />} />
          <Route
            path={routeNames.reportUsers}
            element={
              <PermissionWrapper
                allowedRoles={['owner', 'employee']}
              >
                <Users />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.reportOrders}
            element={
              <PermissionWrapper
                allowedRoles={['owner', 'employee']}
              >
                <Orders />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.reportPayments}
            element={
              <PermissionWrapper
                allowedRoles={['owner', 'employee']}
              >
                <Payments />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.reportRegs}
            element={
              <PermissionWrapper
                allowedRoles={['owner', 'employee']}
              >
                <Regs />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.reportSession}
            element={
              <PermissionWrapper allowedRoles={['owner', 'employee']}>
                <Session />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.reportHit}
            element={
              <PermissionWrapper allowedRoles={['owner', 'employee']}>
                <Hit />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.reportVisitors}
            element={
              <PermissionWrapper allowedRoles={['owner', 'employee']}>
                <Visitors />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.reportHitStat}
            element={
              <PermissionWrapper allowedRoles={['owner', 'employee']}>
                <HitStat />
              </PermissionWrapper>
            }
          />
          <Route path={routeNames.partner} element={<Partner />} />
          <Route
            path={routeNames.crmSchool}
            element={
              <PermissionWrapper allowedRoles={['superadmin']}>
                <CrmSchools />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.crmRequests}
            element={
              <PermissionWrapper allowedRoles={['superadmin']}>
                <CrmRequests />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.crmUsers}
            element={
              <PermissionWrapper allowedRoles={['superadmin']}>
                <CrmUsers />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.crmOrders}
            element={
              <PermissionWrapper allowedRoles={['superadmin']}>
                <CrmOrders />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.crmPayments}
            element={
              <PermissionWrapper allowedRoles={['superadmin']}>
                <CrmPayments />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.crmTariffs}
            element={
              <PermissionWrapper allowedRoles={['superadmin']}>
                <CrmTariffs />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.crmActivity}
            element={
              <PermissionWrapper allowedRoles={['superadmin']}>
                <CrmActivity />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.crmDetails}
            element={
              <PermissionWrapper allowedRoles={['superadmin']}>
                <CrmDetails />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.crmPartner}
            element={
              <PermissionWrapper allowedRoles={['superadmin']}>
                <PartnerCrm />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.crmAnalytics}
            element={
              <PermissionWrapper allowedRoles={['superadmin']}>
                <CrmAnalytics />
              </PermissionWrapper>
            }
          />

          <Route
            path={routeNames.telegramConnect}
            element={<TelegramConnect />}
          />
          <Route path={routeNames.noAccess} element={<NoAccess />} />
          <Route
            path={routeNames.bill}
            element={
              <PermissionWrapper allowedRoles={['owner', 'employee']}>
                <Bill />
              </PermissionWrapper>
            }
          />
          <Route
            path={routeNames.export}
            element={
              <PermissionWrapper
                allowedRoles={['owner', 'employee']}
              >
                <Export />
              </PermissionWrapper>
            }
          />
          <Route path={routeNames.notFound} element={<NotFound />} />
          <Route path={routeNames.error} element={<ErrorPage />} />
        </>
      ) : (
        <>
          <Route path={routeNames.login} element={<Login />} />
          <Route path={routeNames.signup} element={<Signup />} />
          <Route path={routeNames.resetPassword} element={<Reset />} />
          <Route path={routeNames.confirmEmail} element={<ConfirmEmail />} />

          <Route path={routeNames.notFound} element={<NotFound />} />
          <Route path={routeNames.error} element={<ErrorPage />} />

          <Route
            path="*"
            element={<Navigate to={routeNames.login} replace />}
          />
        </>
      )}
    </Routes>
  );
};
